import React, { useCallback, useEffect, useState, useRef, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSupplierStore, JobDetails, SupplierRequest } from '../../lib/supplierStore';
import { toast } from 'sonner';
import { Navigation, ArrowRight, Check, X, Clock, Car, Wrench, Home, Sofa, AlertTriangle, Snowflake, Package, TrendingDown, Image as ImageIcon, ChevronLeft, ChevronRight, ChevronDown, ZoomIn, Sparkles, Send, MessageCircle, Bot, History, Repeat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SupplierLayout } from '../../components/SupplierLayout';
import { AskCustomerAiSlider } from '../../components/AskCustomerAiSlider';
// ================= Helper Components ===================
// CounterEntry component: displays a vertical list with collapsible entries showing name, amount, delta, timestamp, and optional note
function CounterEntry({
  entries
}: {
  entries: Array<{
    name: string;
    amount: number;
    delta?: number;
    timestamp: string;
    note?: string;
  }>;
}) {
  return <ol className="relative pl-7 space-y-3 before:absolute before:left-[11px] before:top-1 before:bottom-1 before:w-px before:bg-amber-200" data-id="element-4766">
      {entries.map((entry, index) => {
      const isYou = entry.name === 'You';
      return <motion.li key={index} initial={{
        opacity: 0,
        x: -4
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: index * 0.04
      }} className="relative" data-id="element-4767">
            <div className={`absolute -left-7 top-1 w-6 h-6 rounded-full flex items-center justify-center font-bold text-[9px] ring-2 ring-white shrink-0 ${isYou ? 'bg-gray-900 text-white' : 'bg-brand-100 text-brand-700'}`} data-id="element-4768">
              {isYou ? 'You' : entry.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap" data-id="element-4769">
              <span className="text-xs font-medium text-gray-700" data-id="element-4770">
                {entry.name} countered
              </span>
              <span className="text-sm font-bold text-gray-900" data-id="element-4771">
                AED {entry.amount.toLocaleString()}
              </span>
              {entry.delta !== undefined && entry.delta !== 0 && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${entry.delta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-id="element-4772">
                  {entry.delta >= 0 ? '+' : ''}
                  {entry.delta.toLocaleString()} AED
                </span>}
              <span className="text-[10px] text-gray-400 ml-auto" data-id="element-4773">
                {entry.timestamp}
              </span>
            </div>
            {entry.note && <p className="mt-0.5 text-[11px] italic text-gray-500 leading-snug" data-id="element-4774">
                "{entry.note}"
              </p>}
          </motion.li>;
    })}
    </ol>;
}
// CounterInput component: input for amount and optional note, with cancel and submit buttons
function CounterInput({
  defaultAmount,
  onCancel,
  onSubmit
}: {
  defaultAmount: number;
  onCancel: () => void;
  onSubmit: (amount: number, note?: string) => void;
}) {
  const [amount, setAmount] = useState<string>(defaultAmount.toString());
  const [note, setNote] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleSend = () => {
    const parsed = parseInt(amount, 10);
    if (!isNaN(parsed) && parsed > 0) {
      onSubmit(parsed, note.trim() || undefined);
    }
  };
  useEffect(() => {
    // focus input field at mount
    inputRef.current?.focus();
  }, []);
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -4
  }} className="mt-2 p-3 rounded-xl border border-gray-200 bg-gray-50 space-y-2" data-id="element-4775">
      {/* Amount input */}
      <div className="flex items-center border border-gray-300 rounded-full px-2 py-1 bg-gray-50" data-id="element-4776">
        <span className="text-gray-500 font-semibold pr-2" data-id="element-4777">AED</span>
        <input ref={inputRef} type="number" inputMode="numeric" pattern="\d*" className="flex-1 bg-transparent outline-none text-gray-700 text-xl font-semibold px-2 py-0.5" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} min={1} autoFocus data-id="element-4778" />
      </div>
      {/* Note input */}
      <input type="text" placeholder="Optional note (e.g. Final offer with 2 movers)" className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" value={note} onChange={e => setNote(e.target.value)} data-id="element-4779" />
      {/* Buttons */}
      <div className="flex justify-between mt-2 space-x-2" data-id="element-4780">
        <button className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors" onClick={onCancel} data-id="element-4781">
          Cancel
        </button>
        <button className="flex-1 bg-gray-900 text-white rounded-full px-4 py-2 hover:bg-gray-700 transition-colors" onClick={handleSend} data-id="element-4782">
          Send counter
        </button>
      </div>
    </motion.div>;
}
// =================== Re-inserted Helper Components ===================
function JobDetailsBlock({
  details
}: {
  details: JobDetails;
}) {
  const baseRow = 'flex items-center gap-2 text-sm text-gray-700';
  const label = 'text-[10px] uppercase tracking-wider font-bold text-gray-400';
  switch (details.kind) {
    case 'car':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4783">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4784">
            <Car className="w-3.5 h-3.5 text-brand-600" data-id="element-4785" />
            Vehicle Details
          </div>
          <div className={baseRow} data-id="element-4786">
            <span className="font-bold text-gray-900" data-id="element-4787">
              {details.year} {details.make} {details.model}
            </span>
          </div>
          {details.plate && <div className={baseRow} data-id="element-4788">
              <span className={label} data-id="element-4789">Plate</span>
              <span className="font-medium" data-id="element-4790">{details.plate}</span>
            </div>}
          {details.condition && <div className="text-xs text-gray-600" data-id="element-4791">{details.condition}</div>}
        </div>;
    case 'heavy-equipment':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4792">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4793">
            <Wrench className="w-3.5 h-3.5 text-brand-600" data-id="element-4794" />
            Equipment Details
          </div>
          <div className="font-bold text-gray-900 text-sm" data-id="element-4795">
            {details.equipment}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4796">
            <span data-id="element-4797">
              <span className={label + ' mr-1'} data-id="element-4798">Weight</span>
              <span className="font-bold text-gray-900" data-id="element-4799">
                {details.weightTons} tons
              </span>
            </span>
            {details.dimensions && <span data-id="element-4800">
                <span className={label + ' mr-1'} data-id="element-4801">Dims</span>
                <span className="font-medium" data-id="element-4802">{details.dimensions}</span>
              </span>}
          </div>
          {details.permitRequired && <div className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-id="element-4803">
              <AlertTriangle className="w-3 h-3" data-id="element-4804" /> Permit required
            </div>}
        </div>;
    case 'home-move':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4805">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4806">
            <Home className="w-3.5 h-3.5 text-brand-600" data-id="element-4807" />
            Move Details
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4808">
            <span data-id="element-4809">
              <span className={label + ' mr-1'} data-id="element-4810">Size</span>
              <span className="font-bold text-gray-900" data-id="element-4811">
                {details.bedrooms} BR
              </span>
            </span>
            {details.floors != null && <span data-id="element-4812">
                <span className={label + ' mr-1'} data-id="element-4813">Floors</span>
                <span className="font-medium" data-id="element-4814">{details.floors}</span>
              </span>}
            <span data-id="element-4815">
              <span className={label + ' mr-1'} data-id="element-4816">Elevator</span>
              <span className="font-medium" data-id="element-4817">
                {details.hasElevator ? 'Yes' : 'No'}
              </span>
            </span>
          </div>
          {details.itemsNote && <div className="text-xs text-gray-600" data-id="element-4818">{details.itemsNote}</div>}
        </div>;
    case 'furniture':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4819">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4820">
            <Sofa className="w-3.5 h-3.5 text-brand-600" data-id="element-4821" />
            Furniture Details
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4822">
            <span data-id="element-4823">
              <span className={label + ' mr-1'} data-id="element-4824">Items</span>
              <span className="font-bold text-gray-900" data-id="element-4825">
                {details.itemCount}
              </span>
            </span>
            {details.largestItem && <span data-id="element-4826">
                <span className={label + ' mr-1'} data-id="element-4827">Largest</span>
                <span className="font-medium" data-id="element-4828">{details.largestItem}</span>
              </span>}
          </div>
          {details.assemblyRequired && <div className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full" data-id="element-4829">
              Assembly required
            </div>}
        </div>;
    case 'recovery':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4830">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4831">
            <AlertTriangle className="w-3.5 h-3.5 text-brand-600" data-id="element-4832" />
            Recovery Details
          </div>
          <div className="font-bold text-gray-900 text-sm" data-id="element-4833">
            {details.vehicleType}
          </div>
          <div className="text-xs text-gray-700" data-id="element-4834">
            <span className={label + ' mr-1'} data-id="element-4835">Reason</span>
            <span className="font-medium" data-id="element-4836">{details.reason}</span>
          </div>
          {details.condition && <div className="text-xs text-gray-600" data-id="element-4837">{details.condition}</div>}
        </div>;
    case 'refrigerated':
      return <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100" data-id="element-4838">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4839">
            <Snowflake className="w-3.5 h-3.5 text-brand-600" data-id="element-4840" />
            Cargo Details
          </div>
          <div className="font-bold text-gray-900 text-sm" data-id="element-4841">
            {details.cargoType}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700" data-id="element-4842">
            <span data-id="element-4843">
              <span className={label + ' mr-1'} data-id="element-4844">Weight</span>
              <span className="font-bold text-gray-900" data-id="element-4845">
                {details.weightKg} kg
              </span>
            </span>
            <span data-id="element-4846">
              <span className={label + ' mr-1'} data-id="element-4847">Temp</span>
              <span className="font-bold text-gray-900" data-id="element-4848">
                {details.tempRange}
              </span>
            </span>
          </div>
        </div>;
    default:
      return <div className="bg-gray-50 rounded-xl p-3 border border-gray-100" data-id="element-4849">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-4850">
            <Package className="w-3.5 h-3.5 text-brand-600" data-id="element-4851" />
            Job Details
          </div>
          <div className="text-sm text-gray-700" data-id="element-4852">
            {(details as any).description}
          </div>
        </div>;
  }
}
function PricePanel({
  originalPrice,
  customerOffer
}: {
  originalPrice: number;
  customerOffer?: number;
}) {
  const hasCounter = typeof customerOffer === 'number' && customerOffer !== originalPrice;
  const delta = hasCounter ? (customerOffer as number) - originalPrice : 0;
  const deltaPct = hasCounter ? Math.round(delta / originalPrice * 100) : 0;
  const isLower = delta < 0;
  return <div className="mb-3" data-id="element-4853">
      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-4854">
        {hasCounter ? 'Customer offer' : 'Price'}
      </div>
      <div className="flex items-baseline gap-2 flex-wrap" data-id="element-4855">
        <div className="text-xl font-display font-bold text-gray-900" data-id="element-4856">
          AED {(hasCounter ? customerOffer : originalPrice)?.toLocaleString()}
        </div>
        {hasCounter && <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${isLower ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-4857">
            <TrendingDown className={`w-3 h-3 ${isLower ? '' : 'rotate-180'}`} data-id="element-4858" />
            {Math.abs(deltaPct)}%
          </span>}
      </div>
      {hasCounter && <div className="text-[11px] text-gray-500 mt-0.5" data-id="element-4859">
          was AED {originalPrice.toLocaleString()}
        </div>}
    </div>;
}
function PhotosStrip({
  photos,
  onOpen
}: {
  photos: string[];
  onOpen: (index: number) => void;
}) {
  return <div className="my-3" data-id="element-4860">
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-4861">
        <ImageIcon className="w-3 h-3" data-id="element-4862" />
        Customer photos
        <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600" data-id="element-4863">
          {photos.length}
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1" data-id="element-4864">
        {photos.map((src, i) => <button key={i} type="button" onClick={() => onOpen(i)} className="group relative shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:border-brand-300 transition-all" aria-label={`View photo ${i + 1}`} data-id="element-4865">
            <img src={src} alt={`Customer photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-4866" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center" data-id="element-4867">
              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4868" />
            </div>
          </button>)}
      </div>
    </div>;
}
// ===================== Main Component =====================
export function SupplierRequests() {
  const navigate = useNavigate();
  const {
    requests,
    acceptRequest,
    declineRequest,
    updateBid,
    submitCounter
  } = useSupplierStore();
  const [tab, setTab] = useState<'incoming' | 'bidding' | 'history'>('incoming');
  const [counterOpenFor, setCounterOpenFor] = useState<string | null>(null);
  const incoming = requests.filter(r => r.status === 'pending' && r.type === 'direct');
  const bidding = requests.filter(r => r.type === 'bidding');
  // Lightbox state — keyed by request id
  const [lightbox, setLightbox] = useState<{
    requestId: string;
    index: number;
  } | null>(null);
  // AI chat state — request id currently open in the AI liaison modal
  const [aiChatRequestId, setAiChatRequestId] = useState<string | null>(null);
  const aiChatRequest = requests.find(r => r.id === aiChatRequestId) || null;
  const [expandedNegotiations, setExpandedNegotiations] = useState<Set<string>>(new Set());
  const toggleNegotiation = (id: string) => {
    setExpandedNegotiations(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const handleAccept = (id: string) => {
    const newJobId = acceptRequest(id);
    if (newJobId) {
      toast.success('Request accepted! Job created.');
      navigate(`/supplier/dispatcher?jobId=${newJobId}`);
    }
  };
  const handleDecline = (id: string) => {
    declineRequest(id);
    toast.success('Request declined.');
  };
  const handleUpdateBid = (id: string, currentBid?: number) => {
    const newBidStr = window.prompt('Enter your new bid (AED):', currentBid?.toString() || '');
    if (newBidStr) {
      const newBid = parseInt(newBidStr, 10);
      if (!isNaN(newBid)) {
        updateBid(id, newBid);
        toast.success('Bid updated successfully.');
      }
    }
  };
  const handleProceed = (id: string) => {
    navigate('/supplier/dispatcher');
  };
  const renderHeaderBar = (req: SupplierRequest, extra?: React.ReactNode) => {
    const isCounter = req.subtype === 'counter';
    const customerRounds = req.negotiation ? req.negotiation.filter(e => e.from === 'customer').length : 0;
    return <div className="flex items-center gap-3 mb-3 flex-wrap" data-id="element-4869">
        {req.type === 'direct' ? isCounter ? <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4870">
              <Repeat className="w-3 h-3" data-id="element-4871" /> Counter-offer
              {customerRounds > 1 ? ` · round ${customerRounds}` : ''}
            </span> : <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4872">
              New request
            </span> : <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${req.status === 'awarded' ? 'bg-green-50 text-green-700' : 'bg-brand-50 text-brand-700'}`} data-id="element-4873">
            {req.status === 'awarded' ? 'Awarded' : 'Live Bidding'}
          </span>}
        <span className="text-xs font-medium text-gray-500 flex items-center gap-1" data-id="element-4874">
          <Clock className="w-3.5 h-3.5" data-id="element-4875" /> {req.requestedTime}
        </span>
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-4876">
          {req.id}
        </span>
        {extra}
        <button onClick={() => setAiChatRequestId(req.id)} className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gradient-to-r from-brand-500 to-purple-500 hover:from-brand-600 hover:to-purple-600 text-white text-[11px] font-bold transition-all shadow-sm" data-id="element-4877">
          <Sparkles className="w-3 h-3" data-id="element-4878" /> Ask customer via AI
        </button>
      </div>;
  };
  return <SupplierLayout data-id="element-4879">
      <div className="max-w-5xl mx-auto" data-id="element-4880">
        <div className="flex items-center justify-between mb-6" data-id="element-4881">
          <h1 className="text-2xl font-display font-bold text-gray-900" data-id="element-4882">
            Requests & Bids
          </h1>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200 pb-px" data-id="element-4883">
          <button onClick={() => setTab('incoming')} className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${tab === 'incoming' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4884">
            Incoming Requests ({incoming.length})
          </button>
          <button onClick={() => setTab('bidding')} className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${tab === 'bidding' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4885">
            Active Bidding ({bidding.length})
          </button>
          <button onClick={() => setTab('history')} className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${tab === 'history' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4886">
            History
          </button>
        </div>

        <div className="space-y-4" data-id="element-4887">
          {tab === 'incoming' && incoming.map(req => <div key={req.id} className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4" data-id="element-4888">
                <div className="flex-1 min-w-0" data-id="element-4889">
                  {renderHeaderBar(req)}
                  <h3 className="font-bold text-gray-900 text-base mb-2" data-id="element-4890">
                    {req.serviceType}
                  </h3>

                  <div className="mb-2" data-id="element-4891">
                    <JobDetailsBlock details={req.jobDetails} data-id="element-4892" />
                  </div>

                  {req.photos && req.photos.length > 0 && <PhotosStrip photos={req.photos} onOpen={i => setLightbox({
              requestId: req.id,
              index: i
            })} data-id="element-4893" />}

                  {req.subtype === 'counter' && req.negotiation && req.negotiation.length > 0 && <div className="my-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white overflow-hidden" data-id="element-4894">
                        <button onClick={() => toggleNegotiation(req.id)} className="w-full flex items-center gap-2 p-4 hover:bg-amber-50/40 transition-colors text-left" aria-expanded={expandedNegotiations.has(req.id)} data-id="element-4895">
                          <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0" data-id="element-4896">
                            <Repeat className="w-4 h-4" data-id="element-4897" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4898">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700" data-id="element-4899">
                              Negotiation history
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-4900">
                              Original quote AED{' '}
                              {req.originalPrice.toLocaleString()} ·{' '}
                              {req.negotiation.length} rounds · You countered{' '}
                              {req.negotiation.filter(e => e.from === 'supplier').length}{' '}
                              time(s)
                            </p>
                          </div>
                          <motion.div animate={{
                  rotate: expandedNegotiations.has(req.id) ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} className="shrink-0" data-id="element-4901">
                            <ChevronDown className="w-4 h-4 text-amber-700" data-id="element-4902" />
                          </motion.div>
                        </button>
                        <AnimatePresence initial={false} data-id="element-4903">
                          {expandedNegotiations.has(req.id) && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-4904">
                              <div className="px-4 pb-4 pt-1 border-t border-amber-200/60" data-id="element-4905">
                                <CounterEntry entries={req.negotiation.map((e, i, arr) => ({
                      name: e.from === 'customer' ? req.customerName : 'You',
                      amount: e.amount,
                      delta: i > 0 ? e.amount - arr[i - 1].amount : undefined,
                      timestamp: e.at,
                      note: e.note
                    }))} data-id="element-4906" />
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>}

                  <div className="flex items-center gap-2 text-xs font-medium text-gray-700 flex-wrap" data-id="element-4907">
                    <Navigation className="w-3 h-3 text-brand-500 shrink-0" data-id="element-4908" />
                    <span className="truncate" data-id="element-4909">{req.pickup}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" data-id="element-4910" />
                    <span className="truncate" data-id="element-4911">{req.dropoff}</span>
                  </div>
                </div>

                <div className="md:w-48 shrink-0 flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0 md:pl-4" data-id="element-4912">
                  <PricePanel originalPrice={req.originalPrice} customerOffer={req.customerOffer} data-id="element-4913" />
                  <div className="flex gap-2 mt-2" data-id="element-4914">
                    <button onClick={() => handleDecline(req.id)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-xs" aria-label="Decline request" data-id="element-4915">
                      <X className="w-3.5 h-3.5" data-id="element-4916" /> Decline
                    </button>
                    <button onClick={() => handleAccept(req.id)} className="flex-1 py-2 bg-gray-900 hover:bg-black text-white rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-xs" aria-label="Accept request" data-id="element-4917">
                      <Check className="w-3.5 h-3.5" data-id="element-4918" /> Accept
                    </button>
                  </div>
                  {req.subtype === 'counter' && <button onClick={() => setCounterOpenFor(cur => cur === req.id ? null : req.id)} className="mt-2 w-full py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-[11px] border border-amber-200" data-id="element-4919">
                      <Repeat className="w-3 h-3" data-id="element-4920" />
                      {counterOpenFor === req.id ? 'Close counter' : 'Send counter'}
                    </button>}
                  <AnimatePresence data-id="element-4921">
                    {counterOpenFor === req.id && <CounterInput defaultAmount={Math.round(((req.customerOffer || req.originalPrice) + req.originalPrice) / 2)} onCancel={() => setCounterOpenFor(null)} onSubmit={(amount, note) => {
                submitCounter(req.id, amount, note);
                setCounterOpenFor(null);
                toast.success(`Counter sent · AED ${amount.toLocaleString()}`);
              }} data-id="element-4922" />}
                  </AnimatePresence>
                </div>
              </div>)}

          {tab === 'bidding' && bidding.map(req => <div key={req.id} className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4" data-id="element-4923">
                <div className="flex-1 min-w-0" data-id="element-4924">
                  {renderHeaderBar(req, req.expiresIn ? <span className="text-xs font-medium text-orange-600 flex items-center gap-1" data-id="element-4925">
                        <Clock className="w-3 h-3" data-id="element-4926" /> Ends in {req.expiresIn}
                      </span> : undefined)}
                  <h3 className="font-bold text-gray-900 text-base mb-2" data-id="element-4927">
                    {req.serviceType}
                  </h3>

                  <div className="mb-2" data-id="element-4928">
                    <JobDetailsBlock details={req.jobDetails} data-id="element-4929" />
                  </div>

                  {req.photos && req.photos.length > 0 && <PhotosStrip photos={req.photos} onOpen={i => setLightbox({
              requestId: req.id,
              index: i
            })} data-id="element-4930" />}

                  <div className="flex items-center gap-2 text-xs font-medium text-gray-700 flex-wrap" data-id="element-4931">
                    <Navigation className="w-3 h-3 text-gray-900 shrink-0" data-id="element-4932" />
                    <span className="truncate" data-id="element-4933">{req.pickup}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" data-id="element-4934" />
                    <span className="truncate" data-id="element-4935">{req.dropoff}</span>
                  </div>
                </div>

                <div className="md:w-40 shrink-0 flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0 md:pl-4" data-id="element-4936">
                  <div className="mb-2" data-id="element-4937">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5" data-id="element-4938">
                      Leading Bid
                    </p>
                    <p className="text-lg font-display font-bold text-gray-900" data-id="element-4939">
                      AED {req.leadingBid?.toLocaleString()}
                    </p>
                  </div>
                  <div className="mb-3" data-id="element-4940">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-brand-600 mb-0.5" data-id="element-4941">
                      Your Bid
                    </p>
                    <p className="text-lg font-display font-bold text-brand-600" data-id="element-4942">
                      AED {req.myBid?.toLocaleString()}
                    </p>
                  </div>
                  {req.status === 'pending' && <button onClick={() => handleUpdateBid(req.id, req.myBid)} className="w-full py-2 bg-gray-900 hover:bg-gray-700 text-white rounded-lg font-bold text-xs transition-colors" data-id="element-4943">
                      Update Bid
                    </button>}
                  {req.status === 'awarded' && <button onClick={() => handleProceed(req.id)} className="w-full py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-xs transition-colors" data-id="element-4944">
                      Proceed to Job
                    </button>}
                </div>
              </div>)}

          {tab === 'history' && <div className="bg-white rounded-2xl p-12 border border-gray-200 shadow-sm text-center" data-id="element-4945">
              <p className="text-sm text-gray-500" data-id="element-4946">No past requests yet.</p>
            </div>}
        </div>
      </div>

      {/* Photo Lightbox */}
      <AnimatePresence data-id="element-4947">
        {lightbox && (() => {
        const req = requests.find(r => r.id === lightbox.requestId);
        if (!req || !req.photos) return null;
        return <PhotoLightbox photos={req.photos} index={lightbox.index} setIndex={i => setLightbox({
          ...lightbox,
          index: i
        })} onClose={() => setLightbox(null)} data-id="element-4948" />;
      })()}
      </AnimatePresence>

      {/* AI-mediated chat */}
      <AnimatePresence data-id="element-4949">
        {aiChatRequest && <AskCustomerAiSlider open={!!aiChatRequest} customerName={aiChatRequest?.customerName || ''} contextLabel={aiChatRequest?.id} onClose={() => setAiChatRequestId(null)} data-id="element-4950" />}
      </AnimatePresence>
    </SupplierLayout>;
}
// REMOVED_DUPLICATE_BLOCK_BELOW
function _unused_DuplicateBlock_DELETE_ME({
  entries
}: {
  entries: Array<{
    name: string;
    amount: number;
    delta?: number;
    timestamp: string;
    note?: string;
  }>;
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className="border-l border-gray-300 max-w-md mx-auto relative pl-6 space-y-3 before:absolute before:left-[11px] before:top-1 before:bottom-1 before:w-px before:bg-amber-200" data-id="element-4951">
      {entries.map((entry, index) => {
      // Determine previous entry delta for delta pill
      const prevEntry = index > 0 ? entries[index - 1] : null;
      const deltaValue = prevEntry && entry.delta !== undefined ? entry.delta : null;
      const showDelta = deltaValue !== null && deltaValue !== 0;
      return <li key={index} className="relative" data-id="element-4952">
            {/* Avatar */}
            <div className="absolute -left-6 top-1 w-5 h-5 ring-2 ring-white rounded-full flex items-center justify-center font-semibold text-xs text-gray-700 bg-brand-100" data-id="element-4953">
              {entry.name.charAt(0).toUpperCase()}
            </div>
            {/* Row */}
            <div className="flex items-center p-2 pl-8 bg-white rounded-lg shadow-sm space-x-2 cursor-pointer" onClick={() => setExpanded(!expanded)} data-id="element-4954">
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:justify-between items-center sm:items-center w-full" data-id="element-4955">
                <div className="flex items-center space-x-2" data-id="element-4956">
                  <p className="font-medium" data-id="element-4957">
                    {entry.name} countered AED {entry.amount.toLocaleString()}
                  </p>
                  {/* Delta pill if delta exists and not zero */}
                  {showDelta && <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${deltaValue! > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-4958">
                      {deltaValue! > 0 ? '+' : ''}
                      {deltaValue} AED
                    </div>}
                </div>
                <span className="text-[10px] text-gray-500" data-id="element-4959">
                  {entry.timestamp}
                </span>
              </div>
            </div>
            {/* Note */}
            {entry.note && <blockquote className="mt-1 ml-8 text-gray-600 italic text-[11px] border-l-2 border-gray-300 pl-2" data-id="element-4960">
                "{entry.note}"
              </blockquote>}
          </li>;
    })}
    </div>;
}
/* DUPLICATE — renamed to avoid identifier collision */
function _unused_CounterInput_DELETE_ME({
  defaultAmount,
  onCancel,
  onSubmit
}: {
  defaultAmount: number;
  onCancel: () => void;
  onSubmit: (amount: number, note?: string) => void;
}) {
  const [amount, setAmount] = useState<string>(defaultAmount.toString());
  const [note, setNote] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleSend = () => {
    const parsed = parseInt(amount, 10);
    if (!isNaN(parsed) && parsed > 0) {
      onSubmit(parsed, note.trim() || undefined);
    }
  };
  useEffect(() => {
    // focus input field at mount
    inputRef.current?.focus();
  }, []);
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -4
  }} className="max-w-sm mx-auto bg-gray-50 rounded-lg p-3 shadow-md space-y-2" data-id="element-4961">
      {/* Amount input */}
      <div className="flex items-center border border-gray-300 rounded-full px-2 py-1 bg-gray-50" data-id="element-4962">
        <span className="text-gray-500 font-semibold pr-2" data-id="element-4963">AED</span>
        <input ref={inputRef} type="number" inputMode="numeric" pattern="\d*" className="flex-1 bg-transparent outline-none text-gray-700 text-xl font-semibold px-2 py-0.5" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} min={1} autoFocus data-id="element-4964" />
      </div>
      {/* Note input */}
      <input type="text" placeholder="Optional note (e.g. Final offer with 2 movers)" className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" value={note} onChange={e => setNote(e.target.value)} data-id="element-4965" />
      {/* Buttons */}
      <div className="flex justify-between mt-2 space-x-2" data-id="element-4966">
        <button className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors" onClick={onCancel} data-id="element-4967">
          Cancel
        </button>
        <button className="flex-1 bg-gray-900 text-white rounded-full px-4 py-2 hover:bg-gray-700 transition-colors" onClick={handleSend} data-id="element-4968">
          Send counter
        </button>
      </div>
    </motion.div>;
}